import React, { useState, useEffect } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING, FONT_SIZE, BORDER_RADIUS, SHADOWS } from '../constants/theme';

const BulkInviteTable = ({ placeholderPlayers, onInvitesChange }) => {
  const { isDesktop } = useResponsive();

  // Initialize rows: one per placeholder player
  const [rows, setRows] = useState([]);

  useEffect(() => {
    // Initialize rows from placeholder players
    const initialRows = placeholderPlayers.map((player) => ({
      player_id: player.id,
      player_name: `${player.first_name} ${player.last_name}`,
      jersey_number: player.jersey_number || '',
      parent_first_name: '',
      parent_last_name: '',
      parent_email: '',
      linked_to_player_id: null, // null = primary parent, uuid = linked to another player
      errors: {},
    }));
    setRows(initialRows);
  }, [placeholderPlayers]);

  const validateEmail = (email) => {
    if (!email) return false;
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateRow = (row, allRows) => {
    const errors = {};

    // If linked to another player, no validation needed (inherits parent)
    if (row.linked_to_player_id) {
      return errors;
    }

    // Primary parent rows need parent info
    if (!row.parent_first_name.trim()) {
      errors.first_name = 'Required';
    }

    if (!row.parent_last_name.trim()) {
      errors.last_name = 'Required';
    }

    if (!row.parent_email) {
      errors.email = 'Email is required';
    } else if (!validateEmail(row.parent_email)) {
      errors.email = 'Invalid email format';
    }

    return errors;
  };

  const validateAllRows = (rowsToValidate) => {
    const updatedRows = rowsToValidate.map((row) => ({
      ...row,
      errors: validateRow(row, rowsToValidate),
    }));
    setRows(updatedRows);

    // Notify parent component
    if (onInvitesChange) {
      onInvitesChange(updatedRows);
    }

    return updatedRows;
  };

  const updateRow = (playerId, field, value) => {
    const updatedRows = rows.map((row) => {
      if (row.player_id === playerId) {
        const updated = { ...row, [field]: value };

        // If linking to another player, clear parent fields
        if (field === 'linked_to_player_id' && value) {
          updated.parent_first_name = '';
          updated.parent_last_name = '';
          updated.parent_email = '';
        }

        // If unlinking (selecting "No link"), keep current parent data
        return updated;
      }
      return row;
    });

    validateAllRows(updatedRows);
  };

  // Get available players for "Same parent as..." dropdown (exclude self and already linked)
  const getAvailableParentLinks = (currentPlayerId) => {
    return rows.filter(
      (r) => r.player_id !== currentPlayerId && !r.linked_to_player_id // Only show primary parents
    );
  };

  // Get all children for a given parent player
  const getLinkedPlayers = (parentPlayerId) => {
    return rows.filter((r) => r.linked_to_player_id === parentPlayerId);
  };

  // Check if a player has children linked to them (is a primary parent)
  const hasLinkedChildren = (playerId) => {
    return rows.some((r) => r.linked_to_player_id === playerId);
  };

  // Group rows: primary parents first, then their linked children
  const getGroupedRows = () => {
    const grouped = [];
    const processed = new Set();

    rows.forEach((row) => {
      if (processed.has(row.player_id)) return;

      if (!row.linked_to_player_id) {
        // Primary parent
        grouped.push({ ...row, isPrimary: true, level: 0 });
        processed.add(row.player_id);

        // Add linked children
        const children = getLinkedPlayers(row.player_id);
        children.forEach((child) => {
          grouped.push({ ...child, isPrimary: false, level: 1 });
          processed.add(child.player_id);
        });
      }
    });

    // Add any orphaned rows (shouldn't happen, but just in case)
    rows.forEach((row) => {
      if (!processed.has(row.player_id)) {
        grouped.push({ ...row, isPrimary: true, level: 0 });
      }
    });

    return grouped;
  };

  const groupedRows = getGroupedRows();

  // Styles
  const tableContainerStyle = {
    overflowX: 'auto',
    marginTop: SPACING.md,
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: FONT_SIZE.sm,
    backgroundColor: COLORS.white,
  };

  const thStyle = {
    padding: SPACING.sm,
    textAlign: 'left',
    borderBottom: `2px solid ${COLORS.gray[300]}`,
    backgroundColor: COLORS.gray[50],
    fontWeight: 600,
    fontSize: FONT_SIZE.sm,
    color: COLORS.gray[700],
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: SPACING.sm,
    borderBottom: `1px solid ${COLORS.gray[200]}`,
    verticalAlign: 'top',
  };

  const indentedTdStyle = {
    ...tdStyle,
    paddingLeft: SPACING.xl,
    backgroundColor: COLORS.gray[50],
  };

  const inputStyle = {
    width: '100%',
    padding: `${SPACING.xs} ${SPACING.sm}`,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: COLORS.gray[300],
    borderRadius: BORDER_RADIUS.sm,
    fontSize: FONT_SIZE.sm,
    boxSizing: 'border-box',
  };

  const inputErrorStyle = {
    ...inputStyle,
    borderColor: COLORS.danger,
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
  };

  const errorTextStyle = {
    color: COLORS.danger,
    fontSize: FONT_SIZE.xs,
    marginTop: SPACING.xs,
  };

  const playerNameStyle = {
    fontWeight: 600,
    color: COLORS.gray[900],
  };

  const linkedIndicatorStyle = {
    fontSize: FONT_SIZE.xs,
    color: COLORS.gray[500],
    marginTop: SPACING.xs,
  };

  const cardStyle = {
    backgroundColor: COLORS.white,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.md,
    marginBottom: SPACING.md,
    boxShadow: SHADOWS.sm,
    border: `1px solid ${COLORS.gray[200]}`,
  };

  const indentedCardStyle = {
    ...cardStyle,
    marginLeft: SPACING.lg,
    backgroundColor: COLORS.gray[50],
    borderLeft: `3px solid ${COLORS.primary}`,
  };

  const cardFieldStyle = {
    marginBottom: SPACING.md,
  };

  const labelStyle = {
    display: 'block',
    fontWeight: 600,
    marginBottom: SPACING.xs,
    fontSize: FONT_SIZE.sm,
    color: COLORS.gray[700],
  };

  if (placeholderPlayers.length === 0) {
    return (
      <div style={{ padding: SPACING.md, color: COLORS.gray[600] }}>
        No placeholder players available. Please create players in Step 1 first.
      </div>
    );
  }

  if (rows.length === 0) {
    return <div>Loading...</div>;
  }

  // Desktop: Table view
  if (isDesktop) {
    return (
      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Player Name</th>
              <th style={thStyle}>Jersey #</th>
              <th style={thStyle}>Parent First Name *</th>
              <th style={thStyle}>Parent Last Name *</th>
              <th style={thStyle}>Email *</th>
              <th style={thStyle}>Same Parent As...</th>
            </tr>
          </thead>
          <tbody>
            {groupedRows.map((row) => {
              const availableLinks = getAvailableParentLinks(row.player_id);
              const isLinked = !!row.linked_to_player_id;
              const hasSiblings = hasLinkedChildren(row.player_id);
              const linkedToPlayer = isLinked
                ? rows.find((r) => r.player_id === row.linked_to_player_id)
                : null;

              return (
                <tr key={row.player_id}>
                  <td style={row.level === 1 ? indentedTdStyle : tdStyle}>
                    <div style={playerNameStyle}>
                      {row.level === 1 && '└─ '}
                      {row.player_name}
                    </div>
                    {isLinked && linkedToPlayer && (
                      <div style={linkedIndicatorStyle}>
                        Using {linkedToPlayer.parent_first_name} {linkedToPlayer.parent_last_name}'s
                        info
                      </div>
                    )}
                  </td>
                  <td style={row.level === 1 ? indentedTdStyle : tdStyle}>{row.jersey_number}</td>
                  <td style={row.level === 1 ? indentedTdStyle : tdStyle}>
                    {isLinked ? (
                      <span style={{ color: COLORS.gray[500], fontSize: FONT_SIZE.sm }}>
                        (inherited)
                      </span>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={row.parent_first_name}
                          onChange={(e) =>
                            updateRow(row.player_id, 'parent_first_name', e.target.value)
                          }
                          style={row.errors.first_name ? inputErrorStyle : inputStyle}
                          placeholder="John"
                        />
                        {row.errors.first_name && (
                          <div style={errorTextStyle}>{row.errors.first_name}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td style={row.level === 1 ? indentedTdStyle : tdStyle}>
                    {isLinked ? (
                      <span style={{ color: COLORS.gray[500], fontSize: FONT_SIZE.sm }}>
                        (inherited)
                      </span>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={row.parent_last_name}
                          onChange={(e) =>
                            updateRow(row.player_id, 'parent_last_name', e.target.value)
                          }
                          style={row.errors.last_name ? inputErrorStyle : inputStyle}
                          placeholder="Smith"
                        />
                        {row.errors.last_name && (
                          <div style={errorTextStyle}>{row.errors.last_name}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td style={row.level === 1 ? indentedTdStyle : tdStyle}>
                    {isLinked ? (
                      <span style={{ color: COLORS.gray[500], fontSize: FONT_SIZE.sm }}>
                        (inherited)
                      </span>
                    ) : (
                      <>
                        <input
                          type="email"
                          value={row.parent_email}
                          onChange={(e) => updateRow(row.player_id, 'parent_email', e.target.value)}
                          style={row.errors.email ? inputErrorStyle : inputStyle}
                          placeholder="parent@example.com"
                        />
                        {row.errors.email && <div style={errorTextStyle}>{row.errors.email}</div>}
                      </>
                    )}
                  </td>
                  <td style={row.level === 1 ? indentedTdStyle : tdStyle}>
                    {hasSiblings ? (
                      <select
                        value=""
                        disabled
                        style={{ ...selectStyle, cursor: 'not-allowed', color: COLORS.gray[500] }}
                      >
                        <option>Siblings will inherit</option>
                      </select>
                    ) : (
                      <select
                        value={row.linked_to_player_id || ''}
                        onChange={(e) =>
                          updateRow(row.player_id, 'linked_to_player_id', e.target.value || null)
                        }
                        style={selectStyle}
                      >
                        <option value="">No link - Enter parent info</option>
                        {availableLinks.map((link) => (
                          <option key={link.player_id} value={link.player_id}>
                            {link.player_name}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // Mobile: Card view
  return (
    <div>
      {groupedRows.map((row) => {
        const availableLinks = getAvailableParentLinks(row.player_id);
        const isLinked = !!row.linked_to_player_id;
        const hasSiblings = hasLinkedChildren(row.player_id);
        const linkedToPlayer = isLinked
          ? rows.find((r) => r.player_id === row.linked_to_player_id)
          : null;

        return (
          <div key={row.player_id} style={row.level === 1 ? indentedCardStyle : cardStyle}>
            <div style={cardFieldStyle}>
              <label style={labelStyle}>
                Player: {row.level === 1 && '└─ '}
                {row.player_name}
                {row.jersey_number && ` (#${row.jersey_number})`}
              </label>
              {isLinked && linkedToPlayer && (
                <div style={linkedIndicatorStyle}>
                  Using {linkedToPlayer.parent_first_name} {linkedToPlayer.parent_last_name}'s info
                </div>
              )}
            </div>

            <div style={cardFieldStyle}>
              <label style={labelStyle}>Same Parent As...</label>
              {hasSiblings ? (
                <select
                  value=""
                  disabled
                  style={{ ...selectStyle, cursor: 'not-allowed', color: COLORS.gray[500] }}
                >
                  <option>Siblings will inherit</option>
                </select>
              ) : (
                <select
                  value={row.linked_to_player_id || ''}
                  onChange={(e) =>
                    updateRow(row.player_id, 'linked_to_player_id', e.target.value || null)
                  }
                  style={selectStyle}
                >
                  <option value="">No link - Enter parent info</option>
                  {availableLinks.map((link) => (
                    <option key={link.player_id} value={link.player_id}>
                      {link.player_name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {!isLinked && (
              <>
                <div style={cardFieldStyle}>
                  <label style={labelStyle}>Parent First Name *</label>
                  <input
                    type="text"
                    value={row.parent_first_name}
                    onChange={(e) => updateRow(row.player_id, 'parent_first_name', e.target.value)}
                    style={row.errors.first_name ? inputErrorStyle : inputStyle}
                    placeholder="John"
                  />
                  {row.errors.first_name && (
                    <div style={errorTextStyle}>{row.errors.first_name}</div>
                  )}
                </div>

                <div style={cardFieldStyle}>
                  <label style={labelStyle}>Parent Last Name *</label>
                  <input
                    type="text"
                    value={row.parent_last_name}
                    onChange={(e) => updateRow(row.player_id, 'parent_last_name', e.target.value)}
                    style={row.errors.last_name ? inputErrorStyle : inputStyle}
                    placeholder="Smith"
                  />
                  {row.errors.last_name && <div style={errorTextStyle}>{row.errors.last_name}</div>}
                </div>

                <div style={cardFieldStyle}>
                  <label style={labelStyle}>Email *</label>
                  <input
                    type="email"
                    value={row.parent_email}
                    onChange={(e) => updateRow(row.player_id, 'parent_email', e.target.value)}
                    style={row.errors.email ? inputErrorStyle : inputStyle}
                    placeholder="parent@example.com"
                  />
                  {row.errors.email && <div style={errorTextStyle}>{row.errors.email}</div>}
                </div>
              </>
            )}

            {isLinked && (
              <div style={{ color: COLORS.gray[500], fontSize: FONT_SIZE.sm, fontStyle: 'italic' }}>
                Parent information inherited from {linkedToPlayer?.player_name}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BulkInviteTable;
